import React from 'react'
import { Link } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { useOutsideClick } from 'rooks'
import { SvgLazy } from '@mobi/component-library/Common/Svg/SvgLazy'
import { TabButton, Tabs } from '@mobi/component-library/Navigation/Tabs'
import { Tooltip } from '@mobi/component-library/Common/V2/Tooltip'
import { PageHeader } from '@mobi/component-library/Navigation/PageHeader'
import { AppRoutes } from '@core/AppRoutes'
import { MainContainer } from '@core/Components/Containers'
import { useSearchParamState } from '@core/Utils/hooks/useSearchParamState'
import type { BetLoopTabNav } from '@core/Areas/BetLoop/types'
import { FollowingFeed } from '@core/Areas/BetLoop/Components/FollowingFeed'
import { ExploreFeed } from '@core/Areas/BetLoop/Components/ExploreFeed'
import { ProfileAvatar } from '@core/Areas/BetLoop/Components/Common/ProfileAvatar'
import { trackBetLoopViewMyProfile } from '@core/Areas/BetLoop/helpers/analytics'
import { betLoopPrincipleProfileQuery } from '@core/Areas/BetLoop/helpers/queries'
import { useOnboardingTooltips } from '@core/Areas/BetLoop/hooks/useOnboardingTooltips'

export const FeedPage = () => {
  const { data: myProfile, isPending } = useQuery(betLoopPrincipleProfileQuery)

  const [currentTab, setCurrentTab] = useSearchParamState<BetLoopTabNav>({
    key: 'tab',
    values: ['explore', 'following'],
    defaultValue: 'explore',
  })

  const viewProfileTooltipRef = React.useRef<HTMLDivElement>(null)

  const { currentTooltipId, onDismissed } = useOnboardingTooltips('bet-loop-view-my-profile')
  const shouldShowTooltip = currentTooltipId === 'bet-loop-view-my-profile'
  useOutsideClick(viewProfileTooltipRef, () => {
    if (shouldShowTooltip) onDismissed('bet-loop-view-my-profile')
  })

  return (
    <MainContainer background='surface300'>
      <PageHeader
        title={<SvgLazy name='BetLoopHeaderLogo' height='3.6rem' color='white' />}
        rightAlignedComponent={
          myProfile ? (
            <Link to={AppRoutes.BetLoopMyProfile} onClick={trackBetLoopViewMyProfile}>
              <div ref={viewProfileTooltipRef}>
                <ProfileAvatar avatar={myProfile?.avatar} color={myProfile.color} />
              </div>
            </Link>
          ) : null
        }
      />

      {shouldShowTooltip && (
        <Tooltip
          color='blue'
          hasTooltipBeenSeen={() => false}
          isHidden={isPending}
          shouldHideCrossIcon
          triggerElRef={viewProfileTooltipRef}
          tooltipId='bet-loop-view-my-profile'
          offsetYPx={7}
          offsetXPx={-55}
          flipElement={true}
          shouldOnlyRenderSingleInstance={true}
          onDismissed={() => onDismissed('bet-loop-view-my-profile')}
        >
          <div>View and edit your</div>
          <div>own profile here</div>
        </Tooltip>
      )}

      <Tabs>
        <TabButton
          content='Explore'
          isSelected={currentTab === 'explore'}
          onClick={() => setCurrentTab('explore')}
        />

        <TabButton
          content='Following'
          isSelected={currentTab === 'following'}
          onClick={() => setCurrentTab('following')}
        />
      </Tabs>

      <div>
        {currentTab === 'explore' ? <ExploreFeed /> : null}

        {currentTab === 'following' ? <FollowingFeed /> : null}
      </div>
    </MainContainer>
  )
}
